import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

const DEFAULTS = {
  // pricing
  baseRatePerKm:          20,
  platformCommissionPct:  10,
  minOrderPriceKr:        500,
  maxOrderPriceKr:        50000,
  invoiceIntervalDays:    14,
  // platform
  invoicePaymentEnabled:       false,
  cardPaymentEnabled:          true,
  requireDriverApproval:       false,
  quarantineDaysAfterRejection: 3,
  maxActiveBidsPerDriver:      10,
  maxRadiusKm:                 100,
  orderExpiryHours:            48,
  maxBidRoundHours:            24,
  notifyOnDispute:             true,
  notifyOnNewUser:             false,
  notifyOnLargeOrder:          true,
  largeOrderThresholdKr:       10000,
  deviationThresholdPct:       5,
  deviationMinWeightTons:      0,
  // maintenance
  maintenanceMode:       false,
  maintenanceMessage:    '',
  flagAllowBidRounds:    true,
  flagAllowFixedPrice:   true,
  flagRequireDriverDocs: false,
};

export function useAdminSettings() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    const pricingRef     = doc(db, 'settings', 'pricing');
    const platformRef    = doc(db, 'settings', 'platform');
    const maintenanceRef = doc(db, 'settings', 'maintenance');

    Promise.all([getDoc(pricingRef), getDoc(platformRef), getDoc(maintenanceRef)])
      .then(([pSnap, plSnap, mSnap]) => {
        if (cancelled) return;
        setSettings({
          ...DEFAULTS,
          ...(pSnap.exists()  ? pSnap.data()  : {}),
          ...(plSnap.exists() ? plSnap.data() : {}),
          ...(mSnap.exists()  ? mSnap.data()  : {}),
        });
      })
      .catch(err => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  async function savePricing(data) {
    const ref = doc(db, 'settings', 'pricing');
    await setDoc(ref, {
      baseRatePerKm:         data.baseRatePerKm,
      platformCommissionPct: data.platformCommissionPct,
      minOrderPriceKr:       data.minOrderPriceKr,
      maxOrderPriceKr:       data.maxOrderPriceKr,
      invoiceIntervalDays:   data.invoiceIntervalDays,
    }, { merge: true });
  }

  async function savePlatform(data) {
    const ref = doc(db, 'settings', 'platform');
    await setDoc(ref, data, { merge: true });
  }

  async function saveMaintenance(data) {
    const ref = doc(db, 'settings', 'maintenance');
    await setDoc(ref, {
      maintenanceMode:       data.maintenanceMode,
      maintenanceMessage:    data.maintenanceMessage,
      flagAllowBidRounds:    data.flagAllowBidRounds,
      flagAllowFixedPrice:   data.flagAllowFixedPrice,
      flagRequireDriverDocs: data.flagRequireDriverDocs,
    }, { merge: true });
  }

  return { settings, loading, error, savePricing, savePlatform, saveMaintenance };
}
